import { useState, useEffect, useCallback, useRef } from "react";
import RFQ_TABS_CONFIG from "./RFQ_TABS_CONFIG";
import {
  REQUIREMENTS_DATA,
  REQUIREMENTS_COUNT_BY_STATUS,
  CREATE_SELLER_REQUIREMENT,
  SELLER_REQUIREMENTS_DATA
} from "../../../lib/API_KEYS";
import { ApiHelper } from "../../../utils/ApiHelper";

// Helper: find tab/sub-tab config for activeTab
export const findTabConfig = (activeTab) => {
  for (const tab of RFQ_TABS_CONFIG) {
    if (tab.key === activeTab) return tab;
    if (tab.subTabs) {
      const sub = tab.subTabs.find((s) => `${tab.key}-${s.key}` === activeTab);
      if (sub) return sub;
    }
  }
  return null;
}

// Helper: get the main tab object that contains the activeTab
export const getActiveMainTab = (activeTab) => RFQ_TABS_CONFIG.find((tab) => {
  if (tab.key === activeTab) return true;
  if (tab.subTabs) return tab.subTabs.some((sub) => `${tab.key}-${sub.key}` === activeTab);
  return false;
});

// Format date utility
export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

export function useRFQData(activeTab, search, currentPage, perPage) {
  const [loading, setLoading] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [counts, setCounts] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const abortControllerRef = useRef(null);

  // --- Fetch paginated requirements for active tab ---
  const fetchRequirements = useCallback(async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    try {
      const tabConfig = findTabConfig(activeTab) || {};
      const status = tabConfig.status || [];

      const queryParams = {
        status: status,
        ...(search ? { search } : {}),
        page: currentPage,
        limit: perPage,
      }

      const apiKey = activeTab === 'new' ? REQUIREMENTS_DATA : SELLER_REQUIREMENTS_DATA

      const response = await new ApiHelper().FetchFromServer(...apiKey, queryParams)

      // Only update state if this request hasn't been cancelled
      if (!signal.aborted) {
        setRequirements(response.data.results || []);
        setTotalResults(response.data.totalResults || 0);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (err) {
      // Don't log abort errors
      if (err.name !== 'AbortError') {
        console.error("Error fetching requirements:", err);
        if (!signal.aborted) {
          setRequirements([]);
          setTotalResults(0);
          setTotalPages(1);
        }
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [activeTab, search, currentPage, perPage]);

  // --- Fetch counts for all tabs/sub-tabs ---
  const fetchCounts = useCallback(async () => {
    try {
      const response = await new ApiHelper().FetchFromServer(
        ...REQUIREMENTS_COUNT_BY_STATUS,
      );
      setCounts(response.data || []);
    } catch (err) {
      console.error("Error fetching RFQ counts:", err);
      setCounts({});
    }
  }, []);

  const editRequirementData = async (data) => {
    const payload = {
      requirementId: data._id,
      buyerRequirementId: data.requirementId,
      buyerTitle: data.title,
      buyerLocation: data.supplierLocation,
      buyerCompanyId: data.companyId,
      buyerCompanyName: data.companyName,
      rfqCreatedAt: data.createdAt,
      status: data.status,
      rejectionReason: data.rejectionReason,
    }

    const response = await new ApiHelper().FetchFromServer(
      ...CREATE_SELLER_REQUIREMENT,
      undefined,
      payload
    );
    fetchCounts();
    fetchRequirements();
    return response;
  };

  // Fetch requirements whenever active tab, search, page, or perPage changes
  useEffect(() => {
    fetchRequirements();
  }, [activeTab, search, currentPage, perPage]);


  // Fetch counts once on mount
  useEffect(() => {
    fetchCounts();
  }, []);

  return {
    loading,
    requirements,
    counts,
    totalResults,
    totalPages,
    formatDate,
    findTabConfig,
    getActiveMainTab,
    editRequirementData
  };
}